import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Clock, Shield, Phone, Calendar, Check, MessageCircle, Users, Award, Star, Video } from "lucide-react";
import DateTimePicker from "./DateTimePicker";
import { useFormNotifications } from "@/hooks/useFormNotifications";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import NameEmailInlineForm from "./hero/NameEmailInlineForm";
import { useAdminSettings } from "@/hooks/useAdminSettings";

// Extend window type for Vimeo Player
declare global {
  interface Window {
    Vimeo?: {
      Player: any;
    };
  }
}
type HeroSectionProps = {
  backgroundUrl?: string;
  videoUrl?: string;
  posterUrl?: string;
  youtubeUrl?: string;
  agentAvatar?: string;
  className?: string;
};

const HeroSection = ({
  backgroundUrl,
  videoUrl,
  posterUrl,
  youtubeUrl,
  agentAvatar,
  className,
}: HeroSectionProps) => {
  const {
    settings
  } = useAdminSettings();
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [selectedDateTime, setSelectedDateTime] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(600); // 10 minutes in seconds
  const [confirmationType, setConfirmationType] = useState<'immediate' | 'scheduled'>('immediate');
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [showPhoneError, setShowPhoneError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNameEmailForm, setShowNameEmailForm] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [detectedCity, setDetectedCity] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
  const {
    visitorInfo
  } = useVisitorTracking();
  const {
    notifyPhoneSubmission,
    notifyCallbackScheduled
  } = useFormNotifications();
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const heroVideoContainerRef = useRef<HTMLDivElement>(null);
  const heroPlayerRef = useRef<any>(null);
  const mobileVideoContainerRef = useRef<HTMLDivElement>(null);
  const mobilePlayerRef = useRef<any>(null);
  useEffect(() => {
    // Detect user location with caching and multi-provider fallback
    const detectLocation = async () => {
      const CACHE_KEY = 'vf_detected_region_v1';
      try {
        // Cached value (24h)
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.label && (Date.now() - parsed.ts) < 24 * 60 * 60 * 1000) {
            setDetectedCity(parsed.label);
            setIsLoadingLocation(false);
            return;
          }
        }

        const fetchWithTimeout = async (url: string, timeoutMs = 2500) => {
          const controller = new AbortController();
          const t = setTimeout(() => controller.abort(), timeoutMs);
          try {
            const res = await fetch(url, { signal: controller.signal });
            clearTimeout(t);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return await res.json();
          } finally {
            clearTimeout(t);
          }
        };

        const providers: Array<{ url: string; map: (d: any) => string | undefined }> = [
          { url: 'https://ipinfo.io/json?token=fdb6cbddb142df', map: (d) => d?.region || d?.city || d?.country },
          { url: 'https://ipapi.co/json/', map: (d) => d?.region || d?.city || d?.country_name },
          { url: 'https://ipwho.is/', map: (d) => (d?.success === false ? undefined : (d?.region || d?.city || d?.country)) },
        ];

        for (const p of providers) {
          try {
            const data = await fetchWithTimeout(p.url);
            const label = (p.map(data) || '').toString();
            if (label && label.length > 1) {
              setDetectedCity(label);
              sessionStorage.setItem(CACHE_KEY, JSON.stringify({ label, ts: Date.now() }));
              setIsLoadingLocation(false);
              return;
            }
          } catch (e) {
            // try next provider
          }
        }
      } catch (error) {
        console.error('Location detection failed:', error);
      } finally {
        setIsLoadingLocation(false);
      }
    };
    detectLocation();

    // Auto-show chat widget after scrolling to bottom
    let scrollTimer: NodeJS.Timeout;
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if user has scrolled to bottom (within 100px tolerance)
      if (scrollTop + windowHeight >= documentHeight - 100) {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (!isChatWidgetOpen) {
            window.dispatchEvent(new CustomEvent('showWhatsAppWidget'));
            setIsChatWidgetOpen(true);
          }
        }, 2000);
      }
    };
    window.addEventListener('scroll', handleScroll);

    // Listen for widget state changes to keep state in sync
    const handleWidgetStateChange = (event: CustomEvent) => {
      setIsChatWidgetOpen(event.detail.isOpen);
    };
    window.addEventListener('whatsAppWidgetStateChange', handleWidgetStateChange as EventListener);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('whatsAppWidgetStateChange', handleWidgetStateChange as EventListener);
      clearTimeout(scrollTimer);
    };
  }, [isChatWidgetOpen]);
  useEffect(() => {
    // Initialize YouTube video player (in form widget) with autoplay + loop
    const initializeHeroPlayer = () => {
      if (!heroVideoContainerRef.current) {
        setTimeout(initializeHeroPlayer, 100);
        return;
      }
      try {
        // Create YouTube iframe
        const iframe = document.createElement('iframe');
        const configuredUrl = (youtubeUrl as string) || ((settings as any)?.video_youtube_url as string) || '';
        // Extract video id from multiple URL formats: embed, watch, youtu.be
        const embedMatch = configuredUrl.match(/embed\/([^?&#/]+)/);
        const watchMatch = configuredUrl.match(/[?&]v=([^?&#/]+)/);
        const shortMatch = configuredUrl.match(/youtu\.be\/([^?&#/]+)/);
        const videoId = (embedMatch?.[1] || watchMatch?.[1] || shortMatch?.[1] || 'wPaQDfiXN7M');
        // Always use embed endpoint to ensure autoplay works
        const embedBase = `https://www.youtube.com/embed/${videoId}`;
        iframe.src = `${embedBase}?autoplay=1&mute=1&playsinline=1&controls=0&modestbranding=1&rel=0&loop=1&playlist=${videoId}`;
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.frameBorder = '0';
        iframe.allow = 'autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        iframe.style.borderRadius = '8px';
        
        heroVideoContainerRef.current.innerHTML = '';
        heroVideoContainerRef.current.appendChild(iframe);
        
        // Set initial playing state for YouTube autoplay
        setIsVideoPlaying(true);
      } catch (error) {
        console.error('Error initializing YouTube player:', error);
      }
    };

    // Initialize the main video player in form (same looping behavior)
    initializeHeroPlayer();
    const timer = setTimeout(() => {
      if (phoneInputRef.current && !mobile) {
        phoneInputRef.current.focus();
      }
    }, 1000);

    // Listen for mini widget events
    const handlePhoneError = () => {
      showPhoneRequiredError();
    };
    const handleImmediateCallbackEvent = () => {
      if (mobile.trim()) {
        const form = document.querySelector('form') as HTMLFormElement;
        if (form) {
          form.dispatchEvent(new Event('submit'));
        }
      } else {
        showPhoneRequiredError();
      }
    };
    const handleScheduleCallbackEvent = () => {
      if (mobile.trim()) {
        setShowDateTimePicker(true);
      } else {
        showPhoneRequiredError();
      }
    };
    window.addEventListener('showPhoneError', handlePhoneError);
    window.addEventListener('triggerImmediateCallback', handleImmediateCallbackEvent);
    window.addEventListener('triggerScheduleCallback', handleScheduleCallbackEvent);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('showPhoneError', handlePhoneError);
      window.removeEventListener('triggerImmediateCallback', handleImmediateCallbackEvent);
      window.removeEventListener('triggerScheduleCallback', handleScheduleCallbackEvent);
      if (heroVideoContainerRef.current) {
        heroVideoContainerRef.current.innerHTML = '';
      }
      if (mobilePlayerRef.current) {
        mobilePlayerRef.current.destroy();
      }
    };
  }, [mobile]);
  useEffect(() => {
    if (showCountdown && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [showCountdown, countdown]);
  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  const showPhoneRequiredError = () => {
    setShowPhoneError(true);
    if (phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
    setTimeout(() => setShowPhoneError(false), 3000);
  };
  const handleImmediateCallback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mobile) {
      showPhoneRequiredError();
      return;
    }
    setIsSubmitting(true);

    // Fire GTM event for phone submission from main hero form
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'cta_phone_submit'
      });
    }
    try {
      await notifyPhoneSubmission(mobile, 'hero_form_immediate');

      // Add system message to chat session
      const sessionId = localStorage.getItem('visitor_session_id');
      if (sessionId) {
        import('@/utils/chatSystemMessages').then(({
          addSystemMessage
        }) => {
          addSystemMessage(sessionId, `📱 Phone number submitted: ${mobile} (Immediate callback requested)`);
        });
      }
      setConfirmationType('immediate');
      setShowCountdown(true);
      setCountdown(600); // Reset to 10 minutes

      // Show name/email form directly without confirmation flash
      setShowNameEmailForm(true);

      // Open WhatsApp chat initiation after requesting callback
      const waText = `Hi, I just requested a callback. My phone is ${mobile}. I want to pick clothes on camera.`;
      window.open('https://wa.me/4915754767196?text=' + encodeURIComponent(waText), '_blank');
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleScheduleCallback = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!mobile) {
      showPhoneRequiredError();
      return;
    }
    setShowDateTimePicker(true);
  };
  const handleCallNow = () => {
    // Fire GTM event for call click from main hero form
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'cta_call_click'
      });
    }
    window.location.href = 'tel:+4915754767196';
  };
  const handleChatNow = () => {
    // Fire GTM event for chat open from main hero form
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'cta_chat_open_click'
      });
    }

    // Toggle the WhatsApp widget
    if (isChatWidgetOpen) {
      // Hide the widget
      setIsChatWidgetOpen(false);
      window.dispatchEvent(new CustomEvent('hideWhatsAppWidget'));
    } else {
      // Show the widget
      setIsChatWidgetOpen(true);
      window.dispatchEvent(new CustomEvent('showWhatsAppWidget'));
    }
  };
  const handleScheduleConfirm = async (confirmationMessage: string) => {
    setSelectedDateTime(confirmationMessage);
    setIsSubmitting(true);
    try {
      // Extract proper date format from confirmation message for backend
      const now = new Date();
      const scheduledDate = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(); // Default to tomorrow

      await notifyCallbackScheduled({
        name: 'Visitor',
        phone: mobile,
        email: email || undefined,
        scheduledDate: scheduledDate,
        notes: `Scheduled from hero section - ${confirmationMessage}`
      });

      // Fire GTM event for scheduled callback
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'cta_callback_scheduled'
        });
      }
      setConfirmationType('scheduled');
      setShowDateTimePicker(false);

      // Show name/email form after scheduling, similar to immediate callback
      setShowNameEmailForm(true);
    } catch (error) {
      console.error('Schedule submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleVideoClick = () => {
    // Toggle video playing state for visual feedback
    setIsVideoPlaying(!isVideoPlaying);
  };
  const handleSarahImageClick = () => {
    // Toggle the WhatsApp widget
    if (isChatWidgetOpen) {
      // Hide the widget
      setIsChatWidgetOpen(false);
      window.dispatchEvent(new CustomEvent('hideWhatsAppWidget'));
    } else {
      // Show the widget
      setIsChatWidgetOpen(true);
      window.dispatchEvent(new CustomEvent('showWhatsAppWidget'));
    }
  };
  const handleVideoToggleMute = (e: React.MouseEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    try {
      video.muted = !video.muted;
      if (video.paused) {
        video.play().catch(() => {});
      }
      setIsMuted(video.muted);
    } catch {
      // no-op
    }
  };
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const location = visitorInfo && visitorInfo.city !== 'Unknown' ? visitorInfo.city : visitorInfo && visitorInfo.region !== 'Unknown' ? visitorInfo.region : 'your area';
  const handleZoomMeeting = () => {
    // Fire GTM event for zoom request
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'cta_zoom_click' });
    }
    // Open WhatsApp to request zoom link
    const text = 'Hi, please share a Zoom meeting link so I can start the live cam-picking session.';
    window.open('https://wa.me/4915754767196?text=' + encodeURIComponent(text), '_blank');
  };
  return <section className={`relative min-h-screen py-8 md:py-12 lg:py-16 bg-cover bg-center bg-no-repeat pt-20 ${className || ''}`} style={{
    backgroundImage: `url(${backgroundUrl || '/lovable-uploads/d5b984a8-9730-4cb4-bce3-4e4f6b36bb65.png'})`
  }}>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/60 md:hidden"></div>
      
      <div className="hidden md:block absolute inset-0 bg-gradient-to-b from-black/70 to-black/60"></div>
      
      <div className="container mx-auto px-4 md:px-6 lg:px-4 relative z-10">
        <div className="text-center lg:text-center">
          <div className="mb-4 md:mb-6 mt-4 md:mt-8">
            <h2 className="text-lg md:text-xl lg:text-2xl font-medium text-[#F4F4F4] mb-2">
              {isLoadingLocation
                ? 'Hey, businesses near you'
                : detectedCity
                  ? <>Hey, businesses in <span className="text-[#D9992B]">{detectedCity}</span></>
                  : 'Hey, businesses near you'}
            </h2>
            <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#F4F4F4] mb-2 leading-tight">Buy Wholesale Vintage Clothes</h1>
            <h2 className="text-base md:text-lg lg:text-xl font-medium text-[#F4F4F4] mb-2">Via Video Call</h2>
            <h3 className="text-base md:text-lg lg:text-xl font-medium text-[#F4F4F4] mb-4">
              Get a call back in <span className="text-[#D9992B]">{(settings as any)?.hero_callback_time || '10 minutes'}</span>
            </h3>
          </div>

          <div className="flex justify-center lg:justify-center mb-4 md:mb-8">
            <div className="w-full md:max-w-sm md:mx-4 md:px-0">
              <Card className="w-full p-4 md:p-5 shadow-2xl border-[#364264]/30 relative overflow-hidden" style={{
              backgroundColor: '#364264'
            }}>
                  {/* Online Status Indicator – moved above video */}
                  {settings.online_status_enabled && (
                    <div className="flex items-center justify-center mb-3">
                      <div
                        className="relative cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40"
                        role="button"
                        aria-label="Open chat"
                        tabIndex={0}
                        onClick={handleSarahImageClick}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSarahImageClick();
                          }
                        }}
                      >
                        <style>{`
                          @keyframes pill-breath { 0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16,185,129,.22);} 50% { transform: scale(1.02); box-shadow: 0 0 0 10px rgba(16,185,129,.06);} }
                          @keyframes pill-shimmer { 0% { transform: translateX(-140%) rotate(18deg);} 100% { transform: translateX(240%) rotate(18deg);} }
                          @keyframes pill-ripple { 0% { transform: scale(1); opacity: .25; } 100% { transform: scale(1.6); opacity: 0; } }
                          @media (prefers-reduced-motion: reduce) {
                            .pill-anim, .pill-shine, .pill-ripple { animation: none !important; }
                          }
                        `}</style>
                        {/* Ripples */}
                        <div className="pill-ripple pointer-events-none absolute inset-0 rounded-full border border-emerald-400/40" style={{ animation: 'pill-ripple 2.4s ease-out infinite' }} />
                        <div className="pill-ripple pointer-events-none absolute inset-0 rounded-full border border-emerald-400/30" style={{ animation: 'pill-ripple 2.4s ease-out infinite', animationDelay: '1.2s' }} />
                        {/* Pill content */}
                        <div className="relative flex items-center gap-2 bg-emerald-500/20 px-3 py-1 rounded-full shadow-lg shadow-emerald-500/20 ring-1 ring-emerald-400/25 transition-transform duration-200 ease-out hover:scale-[1.02] overflow-hidden pill-anim" style={{ animation: 'pill-breath 3.6s ease-in-out infinite' }}>
                          <div className="w-2 h-2 bg-emerald-500 rounded-full motion-safe:animate-pulse"></div>
                          <span className="text-emerald-300 text-sm font-medium">We are online now</span>
                          <div className="pill-shine pointer-events-none absolute top-[-40%] bottom-[-40%] left-0 w-[45%] opacity-30" style={{
                            background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.5) 50%, rgba(255,255,255,0) 100%)',
                            animation: 'pill-shimmer 4.2s ease-in-out infinite'
                          }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Video Player */}
                  <div className="mb-4">
                    <div className="relative w-full h-48 rounded-lg overflow-hidden border-4 border-[#D9992B] shadow-lg">
                      <video
                        className="w-full h-full object-cover"
                        autoPlay
                        muted={isMuted}
                        loop
                        playsInline
                        controls={false}
                        onClick={handleVideoToggleMute}
                        poster={posterUrl || backgroundUrl || '/lovable-uploads/d5b984a8-9730-4cb4-bce3-4e4f6b36bb65.png'}
                      >
                        <source src={(videoUrl as string) || '/videos/campicking.mp4'} type="video/mp4" />
                      </video>
                    </div>
                  </div>

                  {/* Expert Profile */}
                  

                  {isSubmitted ? <div className="text-center">
                      <h2 className="text-2xl font-bold text-[#F4F4F4] mb-4">Thank you!</h2>
                      <p className="text-[#CCCCCC]">We'll contact you soon.</p>
                    </div> : showConfirmation ? <div className="text-center">
                      {confirmationType === 'immediate' ? <>
                          <div className="flex justify-center mb-3">
                            <Check className="w-10 h-10 text-green-500" />
                          </div>
                          <h3 className="text-lg md:text-xl font-bold text-[#F4F4F4] mb-2">
                            Thanks! You'll get a call within 10 minutes.
                          </h3>
                          {showCountdown && <div className="flex items-center justify-center gap-2 mt-3">
                              <Clock className="w-4 h-4 text-[#D9992B]" />
                              <span className="text-xl font-bold text-[#D9992B]">
                                {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                              </span>
                            </div>}
                        </> : <>
                          <div className="flex justify-center mb-3">
                            <Check className="w-10 h-10 text-green-500" />
                          </div>
                          <h3 className="text-lg md:text-xl font-bold text-[#F4F4F4] mb-2">
                            You're booked!
                          </h3>
                          <p className="text-[#F4F4F4] mb-2 text-sm">
                            We'll call you on <span className="text-[#D9992B] font-semibold">{selectedDateTime}</span>
                          </p>
                          <p className="text-xs text-[#CCCCCC]">
                            A confirmation email has been sent.
                          </p>
                        </>}
                    </div> : showNameEmailForm ? <NameEmailInlineForm phoneNumber={mobile} onComplete={() => setShowNameEmailForm(false)} showCountdown={showCountdown && confirmationType === 'immediate'} countdown={countdown} /> : <>
                    
                    
                    {!settings.show_video_instead_of_sara ? <div className="text-center mb-4">
                        <div className="relative mx-auto w-16 h-16 mb-2 cursor-pointer" onClick={handleSarahImageClick}>
                          <div className="absolute inset-0 rounded-full animate-ping bg-green-500/30 scale-110"></div>
                          <img src={agentAvatar || settings.sara_image_url} alt={`${settings.sara_name} - Website Security Expert ready to help with emergency website recovery`} title={`Click to chat with ${settings.sara_name} - Website Expert`} width={64} height={64} className="relative w-16 h-16 rounded-full border-[3px] border-green-500 shadow-lg shadow-green-500/50 object-cover object-top z-10 mx-auto ring-2 ring-green-400/30" />
                          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white z-20"></div>
                        </div>
                        
                        <p className="text-[#CCCCCC] text-sm mb-1">
                          <span className="text-[#F4F4F4] font-bold text-base">{settings.sara_name}</span><br />
                          {settings.sara_role}
                        </p>
                      </div> : <div className="w-full md:max-w-sm md:mx-auto mb-4">
                        <div className="aspect-video rounded-lg shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300 relative cursor-pointer video-container" style={{
                    userSelect: "none"
                  }}>
                          <div ref={heroVideoContainerRef} id="hero-video-inline" className="w-full h-full rounded-lg" style={{
                      pointerEvents: 'none'
                    }} />
                          <div className="absolute inset-0 z-10" onClick={handleVideoClick} />
                          {!isVideoPlaying && <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all">
                              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white/20 transition-all duration-300 hover:scale-110 border border-white/20">
                                <div className="w-0 h-0 border-l-[8px] border-l-white border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-0.5 drop-shadow-lg"></div>
                              </div>
                            </div>}
                        </div>
                        
                        {/* Sara's profile below video */}
                        <div className="mt-4 bg-[#2B2F4A]/50 backdrop-blur-sm rounded-lg p-3 border border-[#364264]/50 relative">
                          {/* Online status indicator in top-right corner */}
                          <div className="absolute top-2 right-2 flex items-center gap-1.5">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-green-400 text-xs font-medium">Online</span>
                          </div>
                          
                          <div className="text-center">
                            <div className="relative mx-auto w-16 h-16 mb-2 cursor-pointer" onClick={handleSarahImageClick}>
                              <div className="absolute inset-0 rounded-full animate-ping bg-green-500/30 scale-110"></div>
                              <img src={agentAvatar || settings.sara_image_url} alt={`${settings.sara_name} - Website Security Expert`} width={64} height={64} className="relative w-16 h-16 rounded-full border-[3px] border-green-500 shadow-lg shadow-green-500/50 object-cover object-top z-10 mx-auto ring-2 ring-green-400/30" />
                              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#2B2F4A] z-20"></div>
                            </div>
                            
                            {/* Name and role on one line */}
                            <div className="text-[#F4F4F4] font-medium text-sm leading-tight">
                              <span className="font-semibold">{settings.sara_name}</span>
                              <span className="text-[#CCCCCC] mx-2">•</span>
                              <span className="text-[#CCCCCC]">{settings.sara_role}</span>
                            </div>
                          </div>
                        </div>
                      </div>}
                    
                    <form onSubmit={handleImmediateCallback} className="space-y-3">
                      <div className="relative">
                        <Input ref={phoneInputRef} type="tel" placeholder={settings.phone_placeholder} value={mobile} onChange={e => setMobile(e.target.value)} className="border-[#747DA1] focus:border-[#D9992B] focus:ring-[#D9992B] bg-[#2B2F4A] text-[#F4F4F4] placeholder:text-[#747DA1] text-center font-medium text-base py-2.5" />
                        {showPhoneError && <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-3 py-2 rounded-lg text-xs whitespace-nowrap z-50 animate-fade-in">
                            <div className="absolute bottom-[-6px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-red-500"></div>
                            Please enter your phone number first
                          </div>}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <Button type="submit" disabled={isSubmitting} className="w-full text-white py-3 text-base font-bold transition-all duration-300 hover:shadow-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 flex items-center justify-center gap-2">
                          <Phone className="w-4 h-4 text-white" />
                          {isSubmitting ? 'Submitting...' : 'Request a callback'}
                        </Button>
                        
                        <Button type="button" onClick={handleScheduleCallback} disabled={isSubmitting} className="w-full text-white py-2.5 text-sm font-semibold transition-all duration-300 hover:shadow-lg bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 flex items-center justify-center gap-2">
                          <Calendar className="w-4 h-4 text-white" />
                          Schedule a callback
                        </Button>

                        <div className="flex items-center my-1">
                          <div className="flex-1 h-px bg-[#747DA1]/30"></div>
                          <span className="px-2 text-[#747DA1] text-xs font-medium bg-[#364264]">OR</span>
                          <div className="flex-1 h-px bg-[#747DA1]/30"></div>
                        </div>

                        <div className="grid grid-cols-2 gap-1.5">
                          <Button type="button" onClick={handleZoomMeeting} className="text-white py-2.5 px-1 text-xs font-semibold transition-all duration-300 hover:shadow-lg bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 flex items-center justify-center gap-1">
                            <Video className="w-3 h-3 text-white" />
                            Request Zoom
                          </Button>

                          <Button type="button" onClick={handleChatNow} className="text-white py-2.5 px-1 text-xs font-semibold transition-all duration-300 hover:shadow-lg bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 flex items-center justify-center gap-1">
                            <svg aria-hidden="true" viewBox="0 0 24 24" className="w-3 h-3 fill-current">
                              <path d="M20.52 3.48A11.8 11.8 0 0012 0C5.37 0 0 5.37 0 12c0 2.1.55 4.16 1.6 5.98L0 24l6.2-1.58A11.9 11.9 0 0012 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52zM12 22a10 10 0 01-5.1-1.39l-.36-.21-3.62.92.97-3.53-.23-.37A10 10 0 1122 12 10 10 0 0112 22zm5.3-6.3c-.29-.15-1.7-.84-1.97-.94-.26-.1-.45-.14-.63.15-.18.28-.72.94-.88 1.12-.16.18-.32.2-.6.07-.29-.15-1.2-.44-2.3-1.4-.85-.76-1.42-1.7-1.58-1.98-.16-.28-.02-.43.12-.58.12-.12.28-.32.43-.48.14-.16.19-.28.29-.47.1-.2.05-.36-.02-.51-.07-.15-.63-1.53-.86-2.1-.23-.55-.46-.48-.63-.49h-.54c-.19 0-.5.07-.76.36s-1 1-1 2.42 1.02 2.8 1.16 3 .2.44.4.66c.19.22.41.49.71.79.99.99 2.39 1.6 2.73 1.78.34.17.54.27.83.42.3.14.57.12.79.07.24-.06 1.48-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.1-.26-.16-.55-.3z"/>
                            </svg>
                            WhatsApp
                          </Button>
                        </div>
                      </div>
                    </form>
                    
                    <p className="text-xs text-[#747DA1] text-center mt-3">
                      🔒 Secure & Private • 24/7 Emergency Support
                    </p>

                    {/* Trust badges below the form */}
                    <div className="flex justify-center gap-2 mt-4 flex-nowrap">
                      <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm rounded-lg px-3 py-2 border border-[#D9992B]/30">
                        <Star className="w-4 h-4 text-[#D9992B]" />
                        <span className="text-sm font-medium text-amber-400">4.6/5 Rating</span>
                      </div>
                      <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm rounded-lg px-3 py-2 border border-[#D9992B]/30">
                        <Users className="w-4 h-4 text-[#D9992B]" />
                        <span className="text-sm font-medium text-amber-400">56 Google Reviews</span>
                      </div>
                    </div>
                  </>}
              </Card>
            </div>
          </div>
          
          {!settings.show_video_instead_of_sara && <div className="md:hidden mb-4">
              <div className="w-full md:max-w-sm md:mx-auto">
                <div className="aspect-video rounded-lg shadow-xl overflow-hidden transition-all duration-300 relative border-4 border-[#D9992B]">
                          <video
                            className="w-full h-full object-cover"
                            autoPlay
                            muted={isMuted}
                            loop
                            playsInline
                            controls={false}
                            onClick={handleVideoToggleMute}
                            poster={posterUrl || backgroundUrl || '/lovable-uploads/d5b984a8-9730-4cb4-bce3-4e4f6b36bb65.png'}
                          >
                            <source src={(videoUrl as string) || '/videos/campicking.mp4'} type="video/mp4" />
                          </video>
                        </div>
              </div>
            </div>}
        </div>
      </div>

      <DateTimePicker isOpen={showDateTimePicker} onClose={() => setShowDateTimePicker(false)} onConfirm={handleScheduleConfirm} phoneNumber={mobile} />
    </section>;
};
export default HeroSection;